/**
 * Translation utilities for dynamic database content
 *
 * Database translation system:
 * - Main tables (merchants, product_types, pricing_rules) contain DEFAULT language
 * - Translation tables contain ONLY non-default languages
 * - Fallback chain: requested language → translation table → default language
 */

/**
 * Get translated value from translations array
 * @param {string} defaultValue - Value from main table (default language)
 * @param {Array} translations - Array of translation objects
 * @param {string} fieldName - Field name to translate (e.g., 'business_name', 'product_name')
 * @param {string} currentLanguage - Current language code (e.g., 'de', 'fr')
 * @param {string} defaultLanguage - Default language code (e.g., 'en')
 * @returns {string} - Translated value or default value
 */
export function getTranslatedValue(defaultValue, translations, fieldName, currentLanguage, defaultLanguage = 'en') {
  // If current language is the default language, return default value
  if (currentLanguage === defaultLanguage) {
    return defaultValue
  }

  // If no translations array, return default value
  if (!translations || !Array.isArray(translations)) {
    return defaultValue
  }

  // Find translation for current language
  const translation = translations.find(t => t.language_code === currentLanguage)

  // If translation exists and has the requested field, return it
  if (translation && translation[fieldName]) {
    return translation[fieldName]
  }

  // Fallback to default value
  return defaultValue
}

/**
 * Get merchant config value with translation support
 * @param {Object} merchantConfig - Merchant config object
 * @param {string} fieldName - Field name to get (e.g., 'business_name', 'header_text_1')
 * @param {string} currentLanguage - Current language code
 * @returns {string|null} - Translated value or default value
 */
export function getMerchantTranslation(merchantConfig, fieldName, currentLanguage) {
  if (!merchantConfig) return null

  const defaultLanguage = merchantConfig.default_language || 'en'
  const defaultValue = merchantConfig[fieldName]
  const translations = merchantConfig.translations || []

  return getTranslatedValue(defaultValue, translations, fieldName, currentLanguage, defaultLanguage)
}

/**
 * Get product type value with translation support
 * @param {Object} product - Product object from apiAdapter
 * @param {string} fieldName - Field name to get (e.g., 'product_name', 'category', 'description')
 * @param {string} currentLanguage - Current language code
 * @param {string} defaultLanguage - Default language code
 * @returns {string|Array|null} - Translated value or default value
 */
export function getProductTranslation(product, fieldName, currentLanguage, defaultLanguage = 'en') {
  if (!product) return null

  const defaultValue = product[fieldName] || product._apiData?.[fieldName]
  const translations = product.translations || product._apiData?.translations || []

  // Special handling for features (JSONB array)
  if (fieldName === 'features') {
    const translated = getTranslatedValue(defaultValue, translations, fieldName, currentLanguage, defaultLanguage)
    // Ensure it's always an array
    return Array.isArray(translated) ? translated : (defaultValue || [])
  }

  return getTranslatedValue(defaultValue, translations, fieldName, currentLanguage, defaultLanguage)
}

/**
 * Get pricing rule value with translation support
 * @param {Object} pricingRule - Pricing rule object
 * @param {string} fieldName - Field name to get (e.g., 'duration_label')
 * @param {string} currentLanguage - Current language code
 * @param {string} defaultLanguage - Default language code
 * @returns {string|null} - Translated value or default value
 */
export function getPricingRuleTranslation(pricingRule, fieldName, currentLanguage, defaultLanguage = 'en') {
  if (!pricingRule) return null

  const defaultValue = pricingRule[fieldName]
  const translations = pricingRule.translations || []

  return getTranslatedValue(defaultValue, translations, fieldName, currentLanguage, defaultLanguage)
}
